import { WeekRangePicker } from 'app/components/common/DatePickers';
import { AgenturNameCell } from 'app/components/shared/AgenturName.cell';
import { Table } from 'app/components/table/table';
import { AggregrationType, ITableColumn, TableModel, TableRow } from 'app/components/table/table.model';
import { useStore } from 'app/context';
import { LeadNowAbos, LeadNowType, LeadNowVM } from 'app/stores/ui/agt.leadnow.ui.store';
import { formatNum } from 'app/utils';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { useEffect, useMemo, useState } from 'react';
import { LeadNowBarChart } from './LeadNowBarChart';
import { useHistoryWithQuery } from 'app/hooks/useHistoryWithQuery';
import routes from 'routes';
import _ from 'lodash';
import { Icon } from 'app/components/common/Icon';

export const LeadNowOptions = observer(() => {
	const { agtLeadNowUiStore } = useStore();

	const onMinChange = (value: number) => {
		runInAction(() => {
			agtLeadNowUiStore.minKwCurrent = value;
		});
	};

	const onMaxChange = (value: number) => {
		runInAction(() => {
			agtLeadNowUiStore.maxKwCurrent = value;
		});
	};

	return (
		<WeekRangePicker minValue={agtLeadNowUiStore.minKw} maxValue={agtLeadNowUiStore.maxKw} opts={agtLeadNowUiStore.kwsOpts} onMinChange={onMinChange} onMaxChange={onMaxChange} queryParam="kw" />
	);
});

interface ILeadnowListe {
	type: LeadNowType;
	abo?: string;
}

export const LeadNowListe = observer((props: ILeadnowListe) => {
	const { berichteUiiStore, agtLeadNowUiStore, agenturListUiStore, session } = useStore();
	const history = useHistoryWithQuery();
	const [hasItems, setHasItems] = useState(false);

	const type = props.type === LeadNowType.brutto ? 'brutto' : 'netto';

	const tm = useMemo(() => {
		const tm = new TableModel<LeadNowVM, number>();
		const cols: ITableColumn<LeadNowVM, number>[] = [];

		cols.push({
			label: 'Agentur',
			path: 'agt.name',
			sortBy: 'agt.name',
			render: AgenturNameCell,
		});

		// if (session.currentUser!.isMulti || session.currentUser!.isAdmin) {
		// 	cols.push({
		// 		label: 'GP',
		// 		path: 'isCurrentLeadNowUserMultiGp',
		// 		render(row) {
		// 			return <>{row.data.isCurrentLeadNowUserMultiGp === 1 ? <Icon iconClass="check-circle" faModifier="far" /> : <></>}</>;
		// 		},
		// 		agg: [
		// 			{
		// 				aggType: AggregrationType.sum,
		// 				path: 'isCurrentLeadNowUserMultiGp',
		// 			},
		// 		],
		// 	});
		// 	cols.push({
		// 		label: 'Teilhaber',
		// 		path: `agt.thCount`,
		// 		agg: [
		// 			{
		// 				aggType: AggregrationType.sum,
		// 			},
		// 		],
		// 	});
		// }

		if (session.currentUser!.isAdmin) {
			cols.push({
				label: 'Status',
				path: 'agenturStatusLabel',
				sortBy: 'agenturStatusLabel',
			});
		}

		const abos = props.abo ? LeadNowAbos.filter((c) => c.path === props.abo) : LeadNowAbos;
		abos.forEach((c) => {
			cols.push({
				label: c.label,
				path: `${c.path}.${type}`,
				format: formatNum,
				agg: [
					{
						path: `${c.path}.${type}`,
						aggType: AggregrationType.sum,
					},
				],
			});
		});

		if (!props.abo) {
			cols.push({
				label: 'Summe',
				path: type,
				cellModifier: 'is-bold',
				agg: [{ aggType: AggregrationType.sum }],
			});
		}

		tm.sortBy = 'agt.name';
		tm.setCols(cols);

		tm.idProperty = 'agtId';
		tm.idType = 'number';

		tm.onRowClick = (row: TableRow<LeadNowVM>) => {
			const agtId = row.data.agt.agtId.toString();
			history.pushWithQuery(routes.AGENTUR.getPath(agtId, 'berichte', 'kunde', 'leadnow', type));
		};

		return tm;
	}, [type, props.abo]);

	useEffect(() => {
		agtLeadNowUiStore.load();
	}, [tm, agtLeadNowUiStore.maxKw, agtLeadNowUiStore.minKw, agtLeadNowUiStore]);

	// Load aggregated data when KW range changes
	useEffect(() => {
		agtLeadNowUiStore.aggregateAgtsByBm(agtLeadNowUiStore.minKw, agtLeadNowUiStore.maxKw);
	}, [agtLeadNowUiStore, agtLeadNowUiStore.maxKw, agtLeadNowUiStore.minKw]);

	// Apply filters whenever data or filter state changes
	useEffect(() => {
		let items = agtLeadNowUiStore.currentKWItems;
		if (items.length === 0) {
			setHasItems(false);
			tm.setRowData([]);
			return;
		}
		if (berichteUiiStore.selectedAgt) {
			const agtId = berichteUiiStore.selectedAgt.agtId;
			items = items.filter((i) => i.agt.agtId === agtId);
		} else {
			items = agenturListUiStore.applyFilterOnAgt(items) as LeadNowVM[];
			items = agenturListUiStore.applyFilterOnGpRelevantAgt(items) as LeadNowVM[];
		}
		const abos = props.abo ? LeadNowAbos.filter((c) => c.path === props.abo) : LeadNowAbos;
		items = items.filter((i) => abos.some((c) => _.get(i, `${c.path}.${type}`) !== 0));
		if (!berichteUiiStore.selectedAgt) {
			agenturListUiStore.updateCounts(items.length);
		}
		setHasItems(items.length > 0);
		tm.setRowData(items);
		tm.sort();
	}, [
		tm,
		berichteUiiStore.selectedAgt,
		agenturListUiStore.filterHasChanged,
		agtLeadNowUiStore.currentKWItems,
	]);

	return (
		<>
			<div className="table-container ">
				<LeadNowOptions />
				{!hasItems || tm.data.length === 0 ? (
					<div className="pad-1rem">Keine AMIS.NOW Leads Teilnahme</div>
				) : (
					<>
						<LeadNowBarChart type={props.type} abo={props.abo} items={tm.data} />
						<Table stickyHeader={true} tm={tm} stickyFooter={true}></Table>
					</>
				)}
			</div>
		</>
	);
});
